/**
 * Space hierarchy loader
 *
 * Shared helper for loading core Space content used by both the sidebar
 * tree and the main Space dashboard. Keeps folders, sprints, tasks, notes,
 * designs, sheets, conversations, and linked chats in sync across surfaces.
 *
 * Primary responsibilities:
 * - Load core hierarchy entities for a Space
 * - Respect archived visibility where supported
 * - Provide a single fetch path for sidebar and dashboard
 *
 * Dependencies:
 * - Space hierarchy API route
 */

import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { SpaceRequestError } from '@/db/spaces'
import type { SpaceTaskVisibilityScope } from '@/lib/spaces/task-visibility'
import type { Tables } from '@/supabase/types'
import type { SpaceTaskVirtualFile } from '@/types/spaces'

// --------------

export type SpaceHierarchyFolder = Tables<'space_folders'>
export type SpaceHierarchySprint = Tables<'space_sprints'>
export type SpaceHierarchyTask = Tables<'space_tasks'>
export type SpaceHierarchyNote = Tables<'space_notes'>
export type SpaceHierarchyDesign = Tables<"space_designs"> & {
  href?: string
}
export type SpaceHierarchyConversation = Tables<'space_conversations'>
export type SpaceHierarchySheet = Tables<"space_sheets"> & {
  href?: string
}

export interface SpaceHierarchyLinkedChat {
  id: string
  chat_id: string
  folder_id?: string | null
  position?: number | null
  chats?: Pick<
    Tables<'chats'>,
    'id' | 'name' | 'created_at' | 'updated_at' | 'model' | 'image_path'
  > | null
}

export interface SpaceHierarchyResult {
  folders: SpaceHierarchyFolder[]
  sprints: SpaceHierarchySprint[]
  tasks: SpaceHierarchyTask[]
  notes: SpaceHierarchyNote[]
  designs: SpaceHierarchyDesign[]
  sheets: SpaceHierarchySheet[]
  conversations: SpaceHierarchyConversation[]
  linkedChats: SpaceHierarchyLinkedChat[]
  taskVirtualFiles: SpaceTaskVirtualFile[]
}

interface LoadOptions {
  showArchived?: boolean
  includeTaskCompletion?: boolean
  forceRefresh?: boolean
  taskScope?: SpaceTaskVisibilityScope
  signal?: AbortSignal
}

// --------------

const HIERARCHY_CACHE_TTL_MS = 5_000
const HIERARCHY_CACHE_MAX_ENTRIES = 24
const hierarchyInflight = new Map<string, Promise<SpaceHierarchyResult>>()
const hierarchyCache = new Map<
  string,
  { expiresAt: number; data: SpaceHierarchyResult }
>()

const getHierarchyCacheKey = (
  spaceId: string,
  options: LoadOptions
) => {
  const showArchived = options.showArchived ?? false
  const includeTaskCompletion = options.includeTaskCompletion ?? true
  const taskScope = options.taskScope ?? "me"
  return `${spaceId}:${showArchived ? "archived" : "active"}:${includeTaskCompletion ? "completion" : "basic"}:${taskScope}`
}

const getCachedHierarchy = (cacheKey: string) => {
  const cached = hierarchyCache.get(cacheKey)
  if (!cached) return null

  if (Date.now() > cached.expiresAt) {
    hierarchyCache.delete(cacheKey)
    return null
  }

  return cached.data
}

const pruneHierarchyCache = () => {
  if (hierarchyCache.size <= HIERARCHY_CACHE_MAX_ENTRIES) {
    return
  }

  const entriesToDelete = hierarchyCache.size - HIERARCHY_CACHE_MAX_ENTRIES
  let deletedCount = 0

  for (const cacheKey of hierarchyCache.keys()) {
    hierarchyCache.delete(cacheKey)
    deletedCount += 1

    if (deletedCount >= entriesToDelete) {
      break
    }
  }
}

/**
 * Drop module-level hierarchy cache entries so TanStack invalidation cannot
 * be satisfied by a stale 5s in-memory hit.
 */
export const clearSpaceHierarchyCache = (spaceId?: string) => {
  if (!spaceId) {
    hierarchyCache.clear()
    hierarchyInflight.clear()
    return
  }

  const prefix = `${spaceId}:`

  for (const cacheKey of [...hierarchyCache.keys()]) {
    if (cacheKey.startsWith(prefix)) {
      hierarchyCache.delete(cacheKey)
    }
  }

  for (const cacheKey of [...hierarchyInflight.keys()]) {
    if (cacheKey.startsWith(prefix)) {
      hierarchyInflight.delete(cacheKey)
    }
  }
}

const readHierarchyRouteError = async (
  response: Response,
  fallback: string
) => {
  const responseText = await response.text()

  if (!responseText) {
    return fallback
  }

  try {
    const payload = JSON.parse(responseText) as unknown

    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
    ) {
      return payload.error
    }
  } catch {
    return responseText
  }

  return responseText
}

const fetchSpaceHierarchy = async (
  spaceId: string,
  showArchived: boolean,
  taskScope: SpaceTaskVisibilityScope,
  signal?: AbortSignal
): Promise<SpaceHierarchyResult> => {
  const searchParams = new URLSearchParams()

  if (showArchived) {
    searchParams.set("includeArchived", "true")
  }

  searchParams.set("taskScope", taskScope)

  const response = await authenticatedFetch(
    searchParams.size > 0
      ? `/api/spaces/${spaceId}/hierarchy?${searchParams.toString()}`
      : `/api/spaces/${spaceId}/hierarchy`,
    {
      cache: "no-store",
      signal
    }
  )

  if (!response.ok) {
    throw new SpaceRequestError(
      await readHierarchyRouteError(response, "Failed to load hierarchy"),
      response.status
    )
  }

  return (await response.json()) as SpaceHierarchyResult
}

// --------------

export const loadSpaceHierarchy = async (
  spaceId: string,
  options: LoadOptions = {}
): Promise<SpaceHierarchyResult> => {
  const showArchived = options.showArchived ?? false
  const taskScope = options.taskScope ?? "me"
  const cacheKey = getHierarchyCacheKey(spaceId, options)

  if (!options.forceRefresh) {
    const cached = getCachedHierarchy(cacheKey)
    if (cached) {
      return cached
    }

    const inflight = hierarchyInflight.get(cacheKey)
    if (inflight) {
      return inflight
    }
  }

  const loadPromise = (async () => {
    const result = await fetchSpaceHierarchy(
      spaceId,
      showArchived,
      taskScope,
      options.signal
    )

    if (options.signal?.aborted) {
      throw new DOMException('Hierarchy load aborted', 'AbortError')
    }

    hierarchyCache.set(cacheKey, {
      data: result,
      expiresAt: Date.now() + HIERARCHY_CACHE_TTL_MS
    })
    pruneHierarchyCache()

    return result
  })()

  hierarchyInflight.set(cacheKey, loadPromise)

  try {
    return await loadPromise
  } finally {
    if (hierarchyInflight.get(cacheKey) === loadPromise) {
      hierarchyInflight.delete(cacheKey)
    }
  }
}
